import React, { useState, useEffect } from 'react';
import { Search, Filter, Printer, FileDown, MoreVertical, Eye, Download, Flag, RefreshCw } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  fetchCardActivity,
  selectTransactions,
  selectTransactionsTotal,
  selectTransactionsLoading,
  setPagination,
  selectTransactionsPagination
} from '../../store/slices/transactionsSlice';
import Button from '../ui/Button';
import './CardActivityPage.css';

interface TransactionActionDropdownProps {
  transactionId: string;
}

const TransactionActionDropdown: React.FC<TransactionActionDropdownProps> = ({ transactionId }) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleAction = (action: string) => {
    console.log(`${action} for transaction ${transactionId}`);
    setIsOpen(false);
  };

  return (
    <div className="transaction-action-dropdown">
      <button
        className="action-trigger"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="More actions"
      >
        <MoreVertical size={16} />
      </button>

      {isOpen && (
        <>
          <div className="dropdown-overlay" onClick={() => setIsOpen(false)} />
          <div className="dropdown-menu">
            <button
              className="dropdown-item"
              onClick={() => handleAction('view-details')}
            >
              <Eye size={14} />
              View Details
            </button>
            <button
              className="dropdown-item"
              onClick={() => handleAction('download-receipt')}
            >
              <Download size={14} />
              Download Receipt
            </button>
            <button
              className="dropdown-item"
              onClick={() => handleAction('flag-transaction')}
            >
              <Flag size={14} />
              Flag Transaction
            </button>
          </div>
        </>
      )}
    </div>
  );
};

const CardActivityPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const transactions = useAppSelector(selectTransactions);
  const total = useAppSelector(selectTransactionsTotal);
  const loading = useAppSelector(selectTransactionsLoading);
  const { page, limit } = useAppSelector(selectTransactionsPagination);

  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    dispatch(fetchCardActivity({ page, limit }));
  }, [dispatch, page, limit]);

  const handlePageChange = (newPage: number) => {
    dispatch(setPagination({ page: newPage }));
  };

  const handleLimitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    dispatch(setPagination({ limit: parseInt(e.target.value), page: 1 }));
  };

  const handleRefresh = () => {
    dispatch(fetchCardActivity({ page, limit }));
  };

  // Local filtering for immediate feedback
  const filteredTransactions = transactions.filter(transaction => {
    const searchStr = searchTerm.toLowerCase();
    return (
      transaction.reference.toLowerCase().includes(searchStr) ||
      transaction.description.toLowerCase().includes(searchStr) ||
      transaction.user?.company?.legalName.toLowerCase().includes(searchStr) ||
      transaction.type.toLowerCase().includes(searchStr)
    );
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: currency === 'NGN' ? 'NGN' : 'NGN',
    }).format(amount);
  };

  return (
    <div className="card-activity-page">
      <div className="transaction-logs-section">
        <div className="section-header">
          <div className="section-title">
            <h2>Transaction Logs</h2>
            {loading && <RefreshCw size={16} className="animate-spin text-gray-400 ml-2" />}
          </div>
          <div className="section-actions">
            <Button variant="secondary" className="refresh-btn" onClick={handleRefresh}>
              <RefreshCw size={16} />
              Refresh
            </Button>
            <Button variant="secondary" className="print-btn">
              <Printer size={16} />
              Print
            </Button>
            <Button variant="secondary" className="csv-btn">
              <FileDown size={16} />
              CSV
            </Button>
          </div>
        </div>

        <div className="table-controls">
          <div className="left-controls">
            <div className="search-container">
              <Search size={16} className="search-icon" />
              <input
                type="text"
                placeholder="Search this table..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
            <Button variant="secondary" className="filter-btn">
              <Filter size={16} />
              <span>Filter</span>
            </Button>
          </div>
          <div className="right-controls">
            <div className="entries-control">
              <span>Show</span>
              <select
                value={limit}
                onChange={handleLimitChange}
                className="entries-select"
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
              <span>entries</span>
            </div>
          </div>
        </div>

        <div className="transactions-table">
          <table>
            <thead>
              <tr>
                <th>Company / User</th>
                <th>TransID</th>
                <th>Type</th>
                <th>Datetime</th>
                <th>Description</th>
                <th>Amount</th>
                <th>Running Balance</th>
                <th>Reference</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredTransactions.length === 0 && !loading ? (
                <tr>
                  <td colSpan={10} className="empty-table">No transactions found</td>
                </tr>
              ) : (
                filteredTransactions.map((transaction) => (
                  <tr key={transaction.id}>
                    <td>
                      <div className="company-info">
                        <span className="company-name">{transaction.user?.company?.legalName || `${transaction.user?.firstName} ${transaction.user?.lastName}` || 'Individual'}</span>
                        <span className="cid-value">{transaction.userId.substring(0, 8)}...</span>
                      </div>
                    </td>
                    <td>
                      <span className="trans-id-value">{transaction.reference.split('-').pop()}</span>
                    </td>
                    <td>
                      <span className={`type-badge ${transaction.type.toLowerCase()}`}>
                        {transaction.type.replace('_', ' ')}
                      </span>
                    </td>
                    <td>
                      <span className="datetime-value">{formatDate(transaction.createdAt)}</span>
                    </td>
                    <td>
                      <span className="transaction-type">{transaction.description || '—'}</span>
                    </td>
                    <td>
                      <span className="amount-value">{formatAmount(transaction.amount, transaction.currency)}</span>
                    </td>
                    <td>
                      <span className="balance-value">{transaction.metadata?.runningBalance ? formatAmount(transaction.metadata.runningBalance, transaction.currency) : '—'}</span>
                    </td>
                    <td>
                      <span className="reference-value">{transaction.reference}</span>
                    </td>
                    <td>
                      <span className={`status-badge ${transaction.status.toLowerCase()}`}>
                        {transaction.status}
                      </span>
                    </td>
                    <td>
                      <div className="transaction-actions">
                        <TransactionActionDropdown transactionId={transaction.id} />
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="table-footer">
          <div className="showing-info">
            Showing {Math.min((page - 1) * limit + 1, total)} to {Math.min(page * limit, total)} of {total} entries
          </div>
          <div className="pagination">
            <button
              className="pagination-btn"
              disabled={page === 1 || loading}
              onClick={() => handlePageChange(page - 1)}
            >
              Previous
            </button>
            <div className="page-numbers">
              <button className="pagination-btn active">{page}</button>
            </div>
            <button
              className="pagination-btn"
              disabled={page * limit >= total || loading}
              onClick={() => handlePageChange(page + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CardActivityPage;
